import React, {useState, useRef, useEffect, Suspense, useMemo} from "react";
import {useNavigate, NavigateFunction, useParams} from "react-router-dom";
import {
    getStation,
    getLightningMapData,
    getReports,
    getTickets,
    getTilts,
    getBatteryValue, getFlaggedVariables
} from "../../services/api.service";
import ITiltPoint from "../../types/tilt_point.type";
import ILightningEvent from "../../types/lighting_event.type";
import IReport from "../../types/report.type";
import ITicket from "../../types/ticket.type";
import IDeployedStation from "../../types/deployed_station.type";
import {MapContainer, TileLayer, Circle, Tooltip} from "react-leaflet";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    ColumnDef,
    SortingState,
} from '@tanstack/react-table';
import BatteryIcon from "../Utils/BatteryIcon";
import IFlaggedVariable from "../../types/flagged_variable.type";
import { Link } from 'react-router-dom';
import Footer from "../Footer/Footer";
import "./Station.css";

// Loading Component
const LoadingPage: React.FC = () => {
    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box">
                                <h4 className="page-title">Station Details</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <div className="card">
                                <div className="card-body text-center py-5">
                                    <div className="mb-4">
                                        <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <h4 className="mb-3">Loading Station Details</h4>
                                    <p className="text-muted mb-0">
                                        Please wait while we fetch the station information...
                                    </p>
                                    <div className="mt-4">
                                        <div className="progress" style={{height: '6px'}}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                role="progressbar"
                                                style={{width: '100%'}}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Component loading skeleton
const ComponentSkeleton: React.FC<{height?: string}> = ({ height = '400px' }) => (
    <div className="d-flex justify-content-center align-items-center" style={{height}}>
        <div className="spinner-border text-muted" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

// Tilt Canvas Component
const TiltCanvas: React.FC<{tilts: ITiltPoint[]}> = React.memo(({ tilts }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const points: ITiltPoint[] = tilts ?? [];
        const size = canvas.width;
        const logicalMin = -2.5;
        const logicalMax = 2.5;
        const logicalRange = logicalMax - logicalMin;
        const scale = size / logicalRange;

        const toCanvas = (x: number, y: number) => ({
            x: (x - logicalMin) * scale,
            y: size - (y - logicalMin) * scale,
        });

        ctx.clearRect(0, 0, size, size);

        // Red background
        ctx.fillStyle = 'red';
        ctx.fillRect(0, 0, size, size);

        // Yellow and green circles
        const center = toCanvas(0, 0);
        ctx.fillStyle = 'yellow';
        ctx.beginPath();
        ctx.arc(center.x, center.y, 2 * scale, 0, 2 * Math.PI);
        ctx.fill();

        ctx.fillStyle = 'green';
        ctx.beginPath();
        ctx.arc(center.x, center.y, 1 * scale, 0, 2 * Math.PI);
        ctx.fill();

        // Draw major tick labels only
        ctx.fillStyle = 'black';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        for (let i = -2; i <= 2; i++) {
            if (i !== 0) {
                ctx.fillText(i.toString(), toCanvas(i, 0).x, center.y + 10); // X labels
                ctx.fillText(i.toString(), center.x + 15, toCanvas(0, i).y); // Y labels
            }
        }

        // Axes
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, center.y);
        ctx.lineTo(size, center.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(center.x, 0);
        ctx.lineTo(center.x, size);
        ctx.stroke();

        // Draw tilt path
        const scaled = points.map(p => toCanvas(p.tiltX, p.tiltY));

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        scaled.forEach((pt, i) => {
            i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y);
        });
        ctx.stroke();

        // Black dots
        ctx.fillStyle = 'black';
        scaled.slice(0, -1).forEach(pt => {
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, 3, 0, 2 * Math.PI);
            ctx.fill();
        });

        // Last point as blue cross
        if (scaled.length > 0) {
            const pt = scaled[scaled.length - 1];
            ctx.strokeStyle = 'blue';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(pt.x - 6, pt.y - 6);
            ctx.lineTo(pt.x + 6, pt.y + 6);
            ctx.moveTo(pt.x + 6, pt.y - 6);
            ctx.lineTo(pt.x - 6, pt.y + 6);
            ctx.stroke();
        }
    }, [tilts]);

    return (
        <canvas
            ref={canvasRef}
            width={400}
            height={400}
            style={{border: '1px solid #ccc'}}
        />
    );
});

// Lightning Map Component
const LightningMap: React.FC<{station: IDeployedStation, events: ILightningEvent[]}> = React.memo(({ station, events }) => {
    const getColor = (val: number) => {
        if (val >= 0.1) return 'white';
        if (val >= 0.01) return 'yellow';
        return 'red';
    };

    return (
        <MapContainer
            center={[
                station.latitude ? parseFloat(station.latitude) : 0,
                station.longitude ? parseFloat(station.longitude) : 0
            ]}
            zoom={6}
            style={{height: '400px', width: '100%'}}
        >
            <TileLayer
                attribution='Google Maps'
                url="https://www.google.cn/maps/vt?lyrs=m@189&gl=cn&x={x}&y={y}&z={z}"
            />
            {events.map((e, idx) => {
                const strikesPerKm2 = 1.27323954473516 * (e.lightningEventsCount / (e.lightningDistanceKm ** 2));
                return (
                    <Circle
                        key={idx}
                        center={[parseFloat(station.latitude), parseFloat(station.longitude)]}
                        radius={50000}
                        color={getColor(strikesPerKm2)}
                        fillOpacity={0.8}
                    >
                        <Tooltip>
                            <div>
                                <br/>
                                Strikes: {e.lightningEventsCount}
                                <br/>
                                Dist: {e.lightningDistanceKm} km
                            </div>
                        </Tooltip>
                    </Circle>
                );
            })}
        </MapContainer>
    );
});

// Reports Table Component
const ReportsTable: React.FC<{reports: IReport[]}> = React.memo(({ reports }) => {
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);

    const reportColumns: ColumnDef<IReport>[] = useMemo(() => [
        {
            header: () => 'Report Type',
            accessorKey: 'reportType',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Report Date',
            accessorKey: 'reportDate',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Technician',
            accessorKey: 'technicianDisplayname',
            cell: info => info.getValue(),
        },
        {
            id: 'details',
            header: '',
            cell: ({row}) => (
                <a
                    href={`https://assetdashboard.tahmo.org/dashboard/reports/view/${row.original.Id}`}
                    className="btn btn-sm btn-outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <i className="mdi mdi-eye"></i>
                </a>
            ),
        }
    ], []);

    const reportTable = useReactTable<IReport>({
        data: reports,
        columns: reportColumns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: 'includesString',
    });

    return (
        <>
            <div className="mb-3">
                <input
                    className="form-control form-control-sm"
                    style={{width: '250px'}}
                    placeholder="Search..."
                    value={globalFilter ?? ''}
                    onChange={e => setGlobalFilter(e.target.value)}
                />
            </div>

            <table className="table dt-responsive nowrap w-100">
                <thead>
                    {reportTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    onClick={header.column.getToggleSortingHandler()}
                                    style={{cursor: 'pointer'}}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                    {header.column.getIsSorted() === 'asc' && ' 🔼'}
                                    {header.column.getIsSorted() === 'desc' && ' 🔽'}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {reportTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {reportTable.getRowModel().rows.length === 0 && (
                        <tr>
                            <td colSpan={reportColumns.length} className="text-center text-muted">
                                No results found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => reportTable.previousPage()}
                        disabled={!reportTable.getCanPreviousPage()}
                    >
                        Previous
                    </button>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => reportTable.nextPage()}
                        disabled={!reportTable.getCanNextPage()}
                    >
                        Next
                    </button>
                </div>
                <span>
                    Page {reportTable.getState().pagination.pageIndex + 1} of{' '}
                    {reportTable.getPageCount()}
                </span>
            </div>
        </>
    );
});

// Tickets Table Component
const TicketsTable: React.FC<{tickets: ITicket[]}> = React.memo(({ tickets }) => {
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);

    const ticketColumns: ColumnDef<ITicket>[] = useMemo(() => [
        {
            header: () => 'Title',
            accessorKey: 'title',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Description',
            accessorKey: 'description',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Priority',
            accessorKey: 'priority',
            cell: info => info.getValue(),
        },
        {
            header: () => 'Date Created',
            accessorKey: 'createdAt',
            cell: info => info.getValue(),
        },
        {
            header: () => 'Status',
            accessorKey: 'status',
            cell: info => info.getValue(),
        },
        {
            id: 'details',
            header: '',
            cell: ({row}) => (
                <a
                    href={`https://tickets.tahmo.org/#/issues/view/${row.original.Id}`}
                    className="btn btn-sm btn-outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <i className="mdi mdi-eye"></i>
                </a>
            ),
        }
    ], []);

    const ticketTable = useReactTable<ITicket>({
        data: tickets,
        columns: ticketColumns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: 'includesString',
    });

    return (
        <>
            <div className="mb-3">
                <input
                    className="form-control form-control-sm"
                    style={{width: '250px'}}
                    placeholder="Search..."
                    value={globalFilter ?? ''}
                    onChange={e => setGlobalFilter(e.target.value)}
                />
            </div>

            <table className="table dt-responsive nowrap w-100">
                <thead>
                    {ticketTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    onClick={header.column.getToggleSortingHandler()}
                                    style={{cursor: 'pointer'}}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                    {header.column.getIsSorted() === 'asc' && ' 🔼'}
                                    {header.column.getIsSorted() === 'desc' && ' 🔽'}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {ticketTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {ticketTable.getRowModel().rows.length === 0 && (
                        <tr>
                            <td colSpan={ticketColumns.length} className="text-center text-muted">
                                No results found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => ticketTable.previousPage()}
                        disabled={!ticketTable.getCanPreviousPage()}
                    >
                        Previous
                    </button>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => ticketTable.nextPage()}
                        disabled={!ticketTable.getCanNextPage()}
                    >
                        Next
                    </button>
                </div>
                <span>
                    Page {ticketTable.getState().pagination.pageIndex + 1} of{' '}
                    {ticketTable.getPageCount()}
                </span>
            </div>
        </>
    );
});

// Flags Table Component
const FlagsTable: React.FC<{flags: IFlaggedVariable[],  stationCode: string }> = React.memo(({ flags, stationCode}) => {
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);

    // Variable name mappings
    const variableNames: Record<string, string> = {
        'wg': 'Wind Gust',
        'te': 'Temperature',
        'ra': 'Solar Radiation',
        'pr': 'Precipitation',
        'lb': 'Battery Level',
        'rh': 'Humidity',
        'ap': 'Pressure',
        'ws': 'Wind Speed',
        'wd': 'Wind Direction',
        'htp': 'Humidity/Temp/Pressure',
        'ty': 'Tilt Y',
        'tx': 'Tilt X',
        'ht': 'Humidity/Temp',
        'le': 'Lightning Events',
        'ld': 'Lightning Distance',
        'se': 'Soil Electrical Conductivity',
        'sm': 'Soil Moisture',
        'st': 'Soil Temperature',
        'lp': 'Logger Ref Pressure',
        'lt': 'Logger Temperature',
        'vp': 'Vapor Pressure',
        'ta': 'Tilt Angle'
    };

    // Function to determine the correct route based on variable with date params
    const getRouteForVariable = (variable: string, flagTime: string): string => {
        const variableLower = variable.toLowerCase();

        // Calculate one week ago from current date
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7); // One week ago

        // Format dates for URL parameters
        const startDateString = encodeURIComponent(startDate.toISOString());
        const endDateString = encodeURIComponent(endDate.toISOString());


        // Precipitation variables
        if (variableLower === 'pr' || variableLower.includes('precipitation')) {
            return `/stations/${stationCode}/timeseries/precipitation?start_date=${startDateString}&end_date=${endDateString}`;
        }

        // Battery variables
        if (variableLower === 'lb' || variableLower.includes('battery')) {
            return `/stations/${stationCode}/timeseries/battery?start_date=${startDateString}&end_date=${endDateString}`;
        }

        // All other sensor variables
        return `/stations/${stationCode}/timeseries/sensor?start_date=${startDateString}&end_date=${endDateString}`;
    };


    const flagColumns: ColumnDef<IFlaggedVariable>[] = useMemo(() => [
        {
            header: () => 'Variable',
            accessorKey: 'variable',
            cell: info => {
                const variable = info.getValue() as string;
                const flagTime = info.row.original.time;
                const displayName = variableNames[variable] || variable.toUpperCase();
                const route = getRouteForVariable(variable, flagTime);

                return (
                    <Link
                        to={route}
                        className="text-primary text-decoration-none fw-medium"
                        title={`View ${displayName} time series from flag start`}
                    >
                        {displayName}
                    </Link>
                );
            },
            filterFn: 'includesString',
        },
        // {
        //     header: () => 'Count',
        //     accessorKey: 'count',
        //     cell: info => {
        //         const count = info.getValue() as number;
        //                 const variable = info.row.original.variable;
        //                 const flagTime = info.row.original.time;
        //                 const route = getRouteForVariable(variable, flagTime);
        //
        //                 let badgeClass = 'bg-success';
        //                 if (count > 50) badgeClass = 'bg-danger';
        //                 else if (count > 20) badgeClass = 'bg-warning';
        //
        //                 return (
        //                     <Link to={route} className="text-decoration-none">
        //                         <span className={`badge ${badgeClass}`}>
        //                             {count}
        //                         </span>
        //                     </Link>
        //                 );
        //     },
        // },
        // {
        //     header: () => 'Last Flagged',
        //     accessorKey: 'time',
        //     cell: info => {
        //          const time = info.getValue() as string;
        //                 const variable = info.row.original.variable;
        //                 const route = getRouteForVariable(variable, time);
        //                 const flagTime = new Date(time);
        //                 const now = new Date();
        //                 const hoursDiff = (now.getTime() - flagTime.getTime()) / (1000 * 60 * 60);
        //
        //                 return (
        //                     <Link to={route} className="text-decoration-none">
        //                         <div>
        //                             <div>{flagTime.toLocaleDateString()} {flagTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
        //                             <small className={`text-${hoursDiff > 24 ? 'danger' : hoursDiff > 12 ? 'warning' : 'success'}`}>
        //                                 ({Math.round(hoursDiff)}h ago)
        //                             </small>
        //                         </div>
        //                     </Link>
        //                 );
        //     },
        // }
    ], []);

    const flagTable = useReactTable<IFlaggedVariable>({
        data: flags,
        columns: flagColumns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: 'includesString',
    });

    return (
        <>
            <div className="mb-3">
                <input
                    className="form-control form-control-sm"
                    style={{width: '250px'}}
                    placeholder="Search flags..."
                    value={globalFilter ?? ''}
                    onChange={e => setGlobalFilter(e.target.value)}
                />
            </div>

            <table className="table dt-responsive nowrap w-100">
                <thead>
                    {flagTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    onClick={header.column.getToggleSortingHandler()}
                                    style={{cursor: 'pointer'}}
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                    {header.column.getIsSorted() === 'asc' && ' 🔼'}
                                    {header.column.getIsSorted() === 'desc' && ' 🔽'}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {flagTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {flagTable.getRowModel().rows.length === 0 && (
                        <tr>
                            <td colSpan={flagColumns.length} className="text-center text-muted">
                                No flags found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => flagTable.previousPage()}
                        disabled={!flagTable.getCanPreviousPage()}
                    >
                        Previous
                    </button>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => flagTable.nextPage()}
                        disabled={!flagTable.getCanNextPage()}
                    >
                        Next
                    </button>
                </div>
                <span>
                    Page {flagTable.getState().pagination.pageIndex + 1} of{' '}
                    {flagTable.getPageCount()}
                </span>
            </div>
        </>
    );
});


const ViewStation: React.FC = () => {
    let navigate: NavigateFunction = useNavigate();
    const [station, setStation] = useState<IDeployedStation | null>(null);
    const params = useParams();
    const stationCode: any = params.stationCode;

    // Loading states for different sections
    const [initialLoading, setInitialLoading] = useState(true);
    const [tiltsLoading, setTiltsLoading] = useState(true);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [ticketsLoading, setTicketsLoading] = useState(true);
    const [batteryLoading, setBatteryLoading] = useState(true);
    const [flagsLoading, setFlagsLoading] = useState(true);

    // Data states
    const [reports, setReports] = useState<IReport[]>([]);
    const [tickets, setTickets] = useState<ITicket[]>([]);
    const [tilts, setTilts] = useState<ITiltPoint[]>([]);
    const [events, setEvents] = useState<ILightningEvent[]>([]);
    const [flags, setFlags] = useState<IFlaggedVariable[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [batteryMean48h, setBatteryMean48h] = useState<number | null>(null);

    const handleCardClick = (category: string) => {
        navigate(`/stations/${stationCode}/timeseries/${category}`);
    };

    useEffect(() => {
        const loadInitialData = async () => {
            if (!stationCode) {
                setError("Station code is required");
                setInitialLoading(false);
                return;
            }

            try {
                // Load essential station data first
                const stationResponse = await getStation(stationCode);
                setStation(stationResponse.data);
                setInitialLoading(false);

                // Start loading secondary data immediately after station loads
                loadSecondaryData(stationResponse.data);

            } catch (err) {
                console.error('Failed to fetch station data:', err);
                setError('Failed to load station details. Please try again.');
                setInitialLoading(false);
            }
        };

        const loadSecondaryData = async (stationData: IDeployedStation) => {
            // Load each component independently with error handling

            // Load tilts
            getTilts(stationCode)
                .then(response => {
                    setTilts(response.data);
                    setTiltsLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load tilts:', err);
                    setTiltsLoading(false);
                });

            // Load lightning events
            getLightningMapData(stationCode)
                .then(response => {
                    setEvents(response.data);
                    setEventsLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load lightning events:', err);
                    setEventsLoading(false);
                });

            // Load reports
            getReports(stationData.id)
                .then(response => {
                    setReports(response.data);
                    setReportsLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load reports:', err);
                    setReportsLoading(false);
                });

            // Load tickets
            getTickets(stationCode)
                .then(response => {
                    setTickets(response.data);
                    setTicketsLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load tickets:', err);
                    setTicketsLoading(false);
                });

            // Load battery data
            getBatteryValue(stationCode, 'mean')
                .then(response => {
                    setBatteryMean48h(response);
                    setBatteryLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load battery data:', err);
                    setBatteryLoading(false);
                });

            // Load flagged variables
            getFlaggedVariables(stationCode)
                .then(response => {
                    // Filter flags with count > 5 as per your backend logic
                    const filteredFlags = response.filter((flag: IFlaggedVariable) => flag.count > 5);
                    setFlags(filteredFlags);
                    setFlagsLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load flagged variables:', err);
                    setFlagsLoading(false);
                });

        };

        loadInitialData();
    }, [stationCode]);

    // Show loading page while initial data is being fetched
    if (initialLoading) {
        return <LoadingPage />;
    }

    if (error) {
        return (
            <div className="content-page">
                <div className="content">
                    <div className="container-fluid">
                        <div className="alert alert-danger" role="alert">
                            <h4 className="alert-heading">Error!</h4>
                            <p>{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box">
                                <h4 className="page-title">Station
                                    - {station?.id} - {station?.name}</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Tilt Component */}
                        <div className="col-xl-3 col-lg-12">
                            <div className="card">
                                <div className="d-flex card-header justify-content-between align-items-center">
                                    <h4 className="header-title">Tilt</h4>
                                </div>
                                <div className="card-body pt-0" style={{maxHeight: '450px'}}>
                                    {tiltsLoading ? (
                                        <ComponentSkeleton />
                                    ) : (
                                        <TiltCanvas tilts={tilts} />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Lightning Map Component */}
                        <div className="col-xl-3 col-lg-12">
                            <div className="card">
                                <div className="d-flex card-header justify-content-between align-items-center">
                                    <h4 className="header-title">Lightning Live Map</h4>
                                </div>
                                <div className="card-body pt-0" style={{maxHeight: '450px'}}>
                                    {eventsLoading || !station ? (
                                        <ComponentSkeleton />
                                    ) : (
                                        <LightningMap station={station} events={events} />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Reports Table */}
                        <div className="col-xl-6 col-lg-12">
                            <div className="card">
                                <div className="d-flex card-header justify-content-between align-items-center">
                                    <h4 className="header-title">Latest Reports</h4>
                                </div>
                                <div className="card-body pt-0" style={{height: '350px', overflowY: 'auto'}}>
                                    {reportsLoading ? (
                                        <ComponentSkeleton height="350px" />
                                    ) : (
                                        <ReportsTable reports={reports} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Battery Level Component */}
                        <div className="col-xl-3 col-lg-12">
                            <div className="card">
                                <div className="d-flex card-header justify-content-between align-items-center">
                                    <h4 className="header-title">Battery Level</h4>
                                </div>
                                <div className="card-body pt-0" style={{maxHeight: '450px', textAlign: 'center'}}>
                                    {batteryLoading ? (
                                        <ComponentSkeleton />
                                    ) : batteryMean48h == null ? (
                                        <div className="text-muted">
                                            <i className="mdi mdi-alert-circle"></i>
                                            <p>No battery data available</p>
                                        </div>
                                    ) : (
                                        <>
                                            <BatteryIcon level={Math.round(batteryMean48h)} />
                                            <small className="text-muted d-block mt-2">48-hour average</small>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* QC Flags Component */}
                        <div className="col-xl-3 col-lg-12">
                            <div className="card">
                                <div className="d-flex card-header justify-content-between align-items-center">
                                    <h4 className="header-title">QC Flags</h4>
                                </div>
                                <div className="card-body pt-0" style={{maxHeight: '450px', overflowY: 'auto'}}>
                                    {flagsLoading ? (
                                        <ComponentSkeleton height="400px" />
                                    ) : flags.length === 0 ? (
                                        <div className="text-center text-success">
                                            <i className="fa fa-check-circle fa-3x mb-3"></i>
                                            <p>No active QC flags</p>
                                            <small className="text-muted">All variables are within normal ranges</small>
                                        </div>
                                    ) : (
                                        <FlagsTable flags={flags} stationCode={stationCode}/>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Tickets Table */}
                        <div className="col-xl-6 col-lg-12">
                            <div className="card">
                                <div className="d-flex card-header justify-content-between align-items-center">
                                    <h4 className="header-title">Latest Tickets</h4>
                                </div>
                                <div className="card-body pt-0" style={{maxHeight: '350px', overflowY: 'auto'}}>
                                    {ticketsLoading ? (
                                        <ComponentSkeleton height="350px" />
                                    ) : (
                                        <TicketsTable tickets={tickets} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Time Series Cards */}
                    <div className="row">
                        <div className="col-lg-4">
                            <div className="card text-bg-primary overflow-hidden"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleCardClick('precipitation')}
                            >
                                <div className="card-body">
                                    <div className="toll-free-box text-center">
                                        <h4 className="text-reset"><i className="fa fa-tint"></i> Precipitation Time Series</h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div className="card text-bg-danger overflow-hidden"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleCardClick('battery')}
                            >
                                <div className="card-body">
                                    <div className="toll-free-box text-center">
                                        <h4 className="text-reset"><i className="fa fa-battery-full"></i> Battery Time Series</h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div className="card text-bg-warning overflow-hidden"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleCardClick('sensor')}
                            >
                                <div className="card-body">
                                    <div className="toll-free-box text-center">
                                        <h4 className="text-reset"><i className="fa fa-thermometer-half"></i> Sensor Time Series</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewStation;